import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useEnsureUserDeployKey, useGithubBranches, usePreviewModules } from '../../../hooks/useAddons'
import type { GithubAuthMode, GitHubPreviewModule } from '../../../api/client'
import { isSelfHosted } from '../../../config/edition'
import { githubApi, meApi } from '../../../api/client'
import type { GitHubRepo } from '../../../api/client'
import { Alert, Badge, Button, Card, Input, Modal, Select, Spinner } from '../../../components/ui'

// AuthPicker — three-mode selector: public / github oauth / deploy key.
//
// Left off `Button` on purpose: these are mutually-exclusive selection tiles
// with a body of hint text, i.e. a radio group wearing button clothes. Turning
// them into filled Buttons would change the interaction, not just the paint.
export function AuthPicker({ auth, setAuth }: { auth: GithubAuthMode; setAuth: (v: GithubAuthMode) => void }) {
  const { t } = useTranslation()
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: meApi.get, staleTime: 5 * 60 * 1000 })
  const connected = !!user?.github_username
  const options: { value: GithubAuthMode; label: string; hint: string; disabled?: boolean }[] = [
    { value: 'public', label: t('detail.addons.authPublic'), hint: t('detail.addons.authPublicHint') },
    // GitHub OAuth is a SaaS-only auth mode (no OAuth app in self-hosted).
    ...(isSelfHosted ? [] : [{ value: 'oauth' as GithubAuthMode, label: t('detail.addons.authOAuth'), hint: connected ? t('detail.addons.authOAuthHint', { username: user!.github_username }) : t('detail.addons.authOAuthDisabled'), disabled: !connected }]),
    { value: 'deploy_key', label: t('detail.addons.authDeployKey'), hint: t('detail.addons.authDeployKeyHint') },
  ]
  return (
    <div className={`grid gap-2 ${isSelfHosted ? 'grid-cols-2' : 'grid-cols-3'}`} role="radiogroup" aria-label={t('detail.addons.authLabel', { defaultValue: 'Repository access' })}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={auth === o.value}
          onClick={() => !o.disabled && setAuth(o.value)}
          disabled={o.disabled}
          className={`text-left border rounded-lg px-3 py-2 transition-colors ${
            auth === o.value ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-gray-300'
          } ${o.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
          <div className="text-xs font-semibold text-gray-900">{o.label}</div>
          <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">{o.hint}</div>
        </button>
      ))}
    </div>
  )
}

// DeployKeyPanel — displays the SSH public key for the given repo, scoped to
// the current user. The key is generated once per (user, repo) and reused
// across every instance/template the user has. Messaging makes that clear so
// the user understands they only add it to GitHub once.
export function DeployKeyPanel({ repoUrl }: { repoUrl: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const valid = /^https?:\/\/github\.com\/[^/]+\/[^/]+/i.test(repoUrl)
  const keyQ = useEnsureUserDeployKey(repoUrl, valid)

  if (!valid) {
    return (
      <Card tinted padded={false} className="p-3 text-xs text-gray-500">
        {t('detail.addons.deployKeyEnterUrl')}
      </Card>
    )
  }
  const pubKey = keyQ.data?.key.public_key
  const created = keyQ.data?.created
  const copy = () => {
    if (!pubKey) return
    navigator.clipboard.writeText(pubKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  // Build the "Add deploy key" link directly to the repo's settings page.
  const settingsUrl = (() => {
    try {
      const u = new URL(repoUrl)
      const parts = u.pathname.split('/').filter(Boolean)
      if (parts.length < 2) return null
      return `https://github.com/${parts[0]}/${parts[1].replace(/\.git$/, '')}/settings/keys/new`
    } catch { return null }
  })()

  return (
    <Alert tone="warning" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-amber-800">
          {created ? t('detail.addons.deployKeyNewTitle') : t('detail.addons.deployKeyExistingTitle')}
        </p>
        <span className="text-[10px] text-amber-700/80 font-medium">{t('detail.addons.deployKeyReusable')}</span>
      </div>
      <p className="text-xs text-amber-700" dangerouslySetInnerHTML={{ __html: t('detail.addons.deployKeyHint') }} />
      {keyQ.isLoading ? (
        <div className="text-xs text-amber-600">{t('detail.addons.deployKeyLoading')}</div>
      ) : pubKey ? (
        <>
          <div className="flex items-start gap-2">
            <code className="flex-1 text-xs bg-white border border-amber-200 rounded px-2 py-1.5 break-all font-mono text-gray-700 leading-relaxed">
              {pubKey}
            </code>
            <Button variant="secondary" size="sm" className="shrink-0" onClick={copy}>
              {copied ? '✓' : t('detail.addons.copy')}
            </Button>
          </div>
          {keyQ.data?.key.fingerprint && (
            <p className="text-[10px] text-amber-700/80 font-mono">{keyQ.data.key.fingerprint}</p>
          )}
          {settingsUrl && (
            <a href={settingsUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 hover:underline">
              {t('detail.addons.deployKeyOpenGithub')} →
            </a>
          )}
        </>
      ) : keyQ.isError ? (
        <div className="text-xs text-red-600">{t('detail.addons.deployKeyError')}</div>
      ) : null}
    </Alert>
  )
}

// ── GitHub install section with repo picker ─────────────────────────────────

// ownerFromGitHubUrl extracts the owner segment for instant client-side OCA
// detection (before the backend probe returns), so the Install button lights up
// the moment someone pastes an OCA URL.
export function ownerFromGitHubUrl(url: string): string {
  const m = url.match(/github\.com\/([^/]+)(?:\/|$)/i)
  return m ? m[1] : ''
}

export function GitHubInstallSection({ instanceId, githubUrl, setGithubUrl, githubRef, setGithubRef, auth, setAuth, handleGitHub, isMutating, busy, actionLockReason, githubMutation, setUploadError, t }: {
  instanceId: string
  githubUrl: string; setGithubUrl: (v: string) => void
  githubRef: string; setGithubRef: (v: string) => void
  auth: GithubAuthMode; setAuth: (v: GithubAuthMode) => void
  handleGitHub: (urlOverride?: string, modules?: string[], refOverride?: string) => void; isMutating: boolean; busy: boolean
  actionLockReason: string | undefined
  githubMutation: { isPending: boolean }
  setUploadError: (v: string) => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const [showPicker, setShowPicker] = useState(false)
  const [repoSearch, setRepoSearch] = useState('')
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: meApi.get, staleTime: 5 * 60 * 1000 })
  const githubConnected = !!user?.github_username

  // Debounce the URL so the branch probe fires when the user stops typing/pasting,
  // not on every keystroke.
  const [debouncedUrl, setDebouncedUrl] = useState(githubUrl)
  useEffect(() => {
    const h = setTimeout(() => setDebouncedUrl(githubUrl), 500)
    return () => clearTimeout(h)
  }, [githubUrl])

  const urlValid = /^https?:\/\/github\.com\/[^/]+\/[^/]+/i.test(debouncedUrl)
  const branchesQ = useGithubBranches(instanceId, debouncedUrl, auth, urlValid)
  const previewMut = usePreviewModules(instanceId)

  // Clear any branch the user picked when the settled repo URL changes, so a
  // selection from a previous repo can't leak into the next one. Fires on repo
  // identity change only — not on every keystroke.
  const prevUrlRef = useRef(debouncedUrl)
  useEffect(() => {
    if (prevUrlRef.current !== debouncedUrl) {
      prevUrlRef.current = debouncedUrl
      setGithubRef('')
    }
  }, [debouncedUrl, setGithubRef])

  const isOCA = branchesQ.data?.is_oca ?? (ownerFromGitHubUrl(githubUrl).toLowerCase() === 'oca')
  const branches = branchesQ.data?.branches ?? []
  const effectiveRef = githubRef || branchesQ.data?.default_branch || ''

  // Module picker modal state.
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerModules, setPickerModules] = useState<GitHubPreviewModule[]>([])

  const selectRepo = (repo: GitHubRepo) => {
    setGithubUrl(`https://github.com/${repo.full_name}`)
    setDebouncedUrl(`https://github.com/${repo.full_name}`)
    setGithubRef(repo.default_branch)
    setAuth(repo.private ? 'oauth' : 'public')
    setShowPicker(false)
    setRepoSearch('')
  }

  // openPicker downloads the archive for the chosen ref and lists its modules,
  // then opens the checkbox modal. Errors reuse the deploy-key / OAuth flip.
  const openPicker = async () => {
    setUploadError('')
    try {
      const res = await previewMut.mutateAsync({ githubUrl, ref: effectiveRef, auth })
      setPickerModules(res.modules)
      setPickerOpen(true)
    } catch (e: unknown) {
      const errData = (e as { response?: { data?: { code?: string; error?: string } } })?.response?.data
      if (errData?.code === 'deploy_key_required') {
        setAuth('deploy_key')
        setUploadError(t('detail.addons.deployKeyNeeded'))
      } else if (errData?.code === 'github_not_connected') {
        setUploadError(t('detail.addons.oauthNotConnected'))
      } else {
        setUploadError(errData?.error ?? t('detail.addonsExtra.githubInstallFailed'))
      }
    }
  }

  const confirmInstall = (modules: string[]) => {
    setPickerOpen(false)
    handleGitHub(undefined, modules, effectiveRef)
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          type="text"
          value={githubUrl}
          onChange={(e) => setGithubUrl(e.target.value)}
          placeholder="https://github.com/owner/repo"
          aria-label={t('detail.addons.tabGithub')}
          className="flex-1"
          fullWidth={false}
        />
        {githubConnected && (
          <Button
            variant="secondary"
            onClick={() => setShowPicker(!showPicker)}
            className="shrink-0"
            icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
              </svg>
            }
          >
            {t('github.browseRepos')}
          </Button>
        )}
      </div>

      {showPicker && githubConnected && (
        <div className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <Input
              type="text"
              value={repoSearch}
              onChange={(e) => setRepoSearch(e.target.value)}
              placeholder={t('github.searchRepos')}
              aria-label={t('github.searchRepos')}
            />
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
            <RepoPickerList showPicker={showPicker} githubConnected={githubConnected} repoSearch={repoSearch} selectRepo={selectRepo} t={t} />
          </div>
        </div>
      )}

      {!githubConnected && !isSelfHosted && (
        <p className="text-xs text-gray-400">
          {t('github.connectHint')}{' '}
          <a href="/profile" className="text-brand-600 hover:text-brand-700 font-medium">{t('github.connectHintLink')}</a>
        </p>
      )}

      {/* Branch: loading spinner → <select> once branches load → text fallback. */}
      {branchesQ.isFetching ? (
        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-400 flex items-center gap-2">
          <Spinner className="w-3.5 h-3.5" />
          {t('detail.addons.loadingBranches')}
        </div>
      ) : branches.length > 0 ? (
        <Select
          value={effectiveRef}
          onChange={(e) => setGithubRef(e.target.value)}
          aria-label={t('detail.addons.branchLabel', { defaultValue: 'Branch' })}
        >
          {(branches.includes(effectiveRef) || !effectiveRef ? branches : [effectiveRef, ...branches]).map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </Select>
      ) : (
        <Input
          type="text"
          value={githubRef}
          onChange={(e) => setGithubRef(e.target.value)}
          placeholder={t('detail.addons.branchPlaceholder')}
          aria-label={t('detail.addons.branchLabel', { defaultValue: 'Branch' })}
        />
      )}

      <AuthPicker auth={auth} setAuth={setAuth} />

      {auth === 'deploy_key' && <DeployKeyPanel repoUrl={githubUrl} />}

      <div className="flex justify-end">
        {/* `oca-star-button` is a deliberate treatment declared after Tailwind's
            utilities in index.css, so it overrides Button's primary fill. */}
        <Button
          onClick={openPicker}
          disabled={!githubUrl || isMutating || busy}
          loading={previewMut.isPending}
          title={actionLockReason}
          className={isOCA ? 'oca-star-button' : undefined}
        >
          {previewMut.isPending
            ? t('detail.addons.loadingModules')
            : isOCA ? (
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden className="oca-star-icon">✦</span>
                {t('detail.addons.installOca')}
              </span>
            ) : t('detail.addons.install')}
        </Button>
      </div>

      {pickerOpen && (
        <ModulePickerModal
          modules={pickerModules}
          repoLabel={`${branchesQ.data?.owner ?? ownerFromGitHubUrl(githubUrl)}/${branchesQ.data?.repo ?? ''}`}
          branch={effectiveRef}
          isOCA={isOCA}
          installing={githubMutation.isPending}
          onConfirm={confirmInstall}
          onClose={() => setPickerOpen(false)}
          t={t}
        />
      )}
    </div>
  )
}

// RepoPickerList — the OAuth "my repos" list, extracted so the query only runs
// while the dropdown is open.
export function RepoPickerList({ showPicker, githubConnected, repoSearch, selectRepo, t }: {
  showPicker: boolean; githubConnected: boolean; repoSearch: string
  selectRepo: (r: GitHubRepo) => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const { data: repos, isLoading: reposLoading } = useQuery({
    queryKey: ['github-repos', repoSearch],
    queryFn: () => githubApi.repos(1, repoSearch),
    enabled: showPicker && githubConnected,
    staleTime: 30 * 1000,
  })
  if (reposLoading) return <div className="px-3 py-4 text-xs text-gray-400 text-center">{t('github.loadingRepos')}</div>
  if (!repos || repos.length === 0) return <div className="px-3 py-4 text-xs text-gray-400 text-center">{t('github.noRepos')}</div>
  return (
    <>
      {repos.map((repo) => (
        // A list row, not an action: left as a plain button on purpose.
        <button key={repo.full_name} type="button" onClick={() => selectRepo(repo)} className="w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">{repo.full_name}</span>
            {repo.private && <Badge tone="warning" className="shrink-0">{t('github.private')}</Badge>}
          </div>
          {repo.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{repo.description}</p>}
        </button>
      ))}
    </>
  )
}

// ModulePickerModal — checkbox picker for a multi-module repo, with a
// select-all/none master checkbox. The user's selection is what gets installed.
export function ModulePickerModal({ modules, repoLabel, branch, isOCA, installing, onConfirm, onClose, t }: {
  modules: GitHubPreviewModule[]
  repoLabel: string; branch: string; isOCA: boolean; installing: boolean
  onConfirm: (modules: string[]) => void
  onClose: () => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const masterRef = useRef<HTMLInputElement>(null)
  const allSelected = modules.length > 0 && selected.size === modules.length
  const someSelected = selected.size > 0 && selected.size < modules.length
  useEffect(() => {
    if (masterRef.current) masterRef.current.indeterminate = someSelected
  }, [someSelected])

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name); else next.add(name)
      return next
    })
  }
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(modules.map((m) => m.name)))
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      className={isOCA ? 'oca-star-border' : undefined}
      title={
        <span className="inline-flex items-center gap-2">
          {isOCA && <span aria-hidden className="oca-star-icon text-amber-500">✦</span>}
          {t('detail.addons.pickModulesTitle')}
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            onClick={() => onConfirm(Array.from(selected))}
            disabled={selected.size === 0}
            loading={installing}
            className={isOCA ? 'oca-star-button' : undefined}
          >
            {installing ? t('detail.addons.installing') : t('detail.addons.installSelected', { n: selected.size })}
          </Button>
        </>
      }
    >
      <p className="text-xs text-gray-500 font-mono truncate -mt-2 mb-3">{repoLabel}{branch ? ` · ${branch}` : ''}</p>

      {/* Raw checkboxes, not the Checkbox primitive: the master one needs a ref
          to set `indeterminate`, which Checkbox does not forward, and the rows
          carry a two-line composite label the primitive's shape does not fit. */}
      <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 cursor-pointer select-none">
        <input ref={masterRef} type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
        <span className="text-sm font-medium text-gray-700">{allSelected ? t('detail.addons.selectNone') : t('detail.addons.selectAll')}</span>
        <span className="text-xs text-gray-400 ml-auto">{t('detail.addons.selectedCount', { n: selected.size, total: modules.length })}</span>
      </label>

      {/* The list scrolls under a pinned master row — so `scrollable` on Modal,
          which scrolls the whole body, is deliberately not used here. */}
      <div className="mt-2 max-h-[45vh] overflow-y-auto divide-y divide-gray-50">
        {modules.map((m) => (
          <label key={m.name} className="flex items-start gap-3 px-1 py-2.5 cursor-pointer hover:bg-gray-50">
            <input type="checkbox" checked={selected.has(m.name)} onChange={() => toggle(m.name)} className="mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 truncate">{m.manifest_name || m.name}</span>
                {m.already_installed && <Badge tone="success" className="shrink-0">{t('detail.addons.alreadyInstalled')}</Badge>}
              </div>
              <p className="text-xs text-gray-400 font-mono truncate">{m.name}{m.version ? ` · ${m.version}` : ''}</p>
            </div>
          </label>
        ))}
      </div>
    </Modal>
  )
}
